import React, { useCallback, useState } from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { Select } from "antd";
import CalendarSelect from "components/calendar-select";

import { DateRange } from "@material-ui/pickers/DateRangePicker/RangeTypes";

import { getErrorMessage } from "../utils/distribuicao-varietal-utils";
import useAdvancedFiltersDistribuicaoVarietal from "../hooks/useAdvancedFiltersDistribuicaoVarietal";
import { useFilters } from "core/features/filters/store";
import useSafraPeriodo from "core/features/talhoes/hooks/useSafraPeriodo";

interface DistribuicaoVarietalAdvancedFiltersProps {
  handleCloseModal: () => void;
}

export interface AdvancedFiltersProps {
  plantio: DateRange<Date>;
  emergencia: DateRange<Date>;
  colheita: DateRange<Date>;
  previsaoDeColheita: DateRange<Date>;
  safraPeriodos?: string[];
}

const DistribuicaoVarietalAdvancedFilters = ({
  handleCloseModal,
}: DistribuicaoVarietalAdvancedFiltersProps) => {
  const {
    items: {
      filterColheita,
      filterEmergencia,
      filterPlantio,
      filterPrevisaoDeColheita,
      filterSafraPeriodos,
    },
    onChange,
    clearFilters,
  } = useAdvancedFiltersDistribuicaoVarietal();

  const { filters } = useFilters();

  const safraId = filters?.safra?.id ?? "";
  const unidadeId = filters?.unidade?.id ?? "";

  const { data } = useSafraPeriodo(unidadeId, safraId);
  const items = data?.items ?? [];
  const options = items?.map(item => ({ value: item.id, label: item.nomeOriginal }));

  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersProps>({
    plantio: filterPlantio,
    previsaoDeColheita: filterPrevisaoDeColheita,
    emergencia: filterEmergencia,
    colheita: filterColheita,
    safraPeriodos: filterSafraPeriodos,
  });

  const handleChangePeriodoPlantio = (props: DateRange<Date>) => {
    setAdvancedFilters({
      ...advancedFilters,
      plantio: props,
    });
  };

  const handleChangePeriodoPrevisaoDeColheita = (props: DateRange<Date>) => {
    setAdvancedFilters({
      ...advancedFilters,
      previsaoDeColheita: props,
    });
  };

  const handleChangePeriodoColheita = (props: DateRange<Date>) => {
    setAdvancedFilters({
      ...advancedFilters,
      colheita: props,
    });
  };

  const handleChangePeriodoEmergencia = (props: DateRange<Date>) => {
    setAdvancedFilters({
      ...advancedFilters,
      emergencia: props,
    });
  };

  const handleChangeSafraPeriodos = (props: string[]) => {
    setAdvancedFilters({
      ...advancedFilters,
      safraPeriodos: props,
    });
  };

  const checkIfSubmitButtonIsDisabled = useCallback(() => {
    const filters = [
      advancedFilters.colheita,
      advancedFilters.emergencia,
      advancedFilters.plantio,
      advancedFilters.previsaoDeColheita,
    ];

    const fieldsWithErrors = filters.filter((field) => {
      const fieldMessages = getErrorMessage(field);
      if (fieldMessages.finalDate || fieldMessages.initialDate) {
        return field;
      }

      return 0;
    });

    return !!fieldsWithErrors.length;
  }, [advancedFilters]);

  return (
    <>
      <Text
        fontSize="16px"
        color="green.600"
        fontWeight="bold"
        margin="20px 20px 10px"
      >
        {" "}
        Filtros Avançados
      </Text>
      <CalendarSelect
        errors={useCallback(
          () => getErrorMessage(advancedFilters.plantio),
          [advancedFilters.plantio]
        )()}
        title="Plantio"
        value={advancedFilters.plantio}
        onChange={handleChangePeriodoPlantio}
      />
      <CalendarSelect
        errors={useCallback(
          () => getErrorMessage(advancedFilters.previsaoDeColheita),
          [advancedFilters.previsaoDeColheita]
        )()}
        title="Previsão de Colheita"
        value={advancedFilters.previsaoDeColheita}
        onChange={handleChangePeriodoPrevisaoDeColheita}
      />{" "}
      <CalendarSelect
        errors={useCallback(
          () => getErrorMessage(advancedFilters.colheita),
          [advancedFilters.colheita]
        )()}
        title="Colheita"
        value={advancedFilters.colheita}
        onChange={handleChangePeriodoColheita}
      />{" "}
      <CalendarSelect
        errors={useCallback(
          () => getErrorMessage(advancedFilters.emergencia),
          [advancedFilters.emergencia]
        )()}
        title="Emergência"
        value={advancedFilters.emergencia}
        onChange={handleChangePeriodoEmergencia}
      />
      <Box margin="8px 15px" padding={0}>
        <Text color="gray.700" fontSize="16px" marginRight="5px" marginBottom="15px">
          Safra Período
        </Text>
        <Select
          allowClear
          size="large"
          mode="multiple"
          options={options}
          placeholder="Safra Período"
          onChange={handleChangeSafraPeriodos}
          style={{ width: "100%" }}
          maxTagCount={0}
          maxTagPlaceholder={() => `Safra Períodos (${advancedFilters?.safraPeriodos?.length || 0})`}
        />
      </Box>
      <Flex justifyContent="flex-end" padding="10px 20px">
        <Button
          background="transparent"
          onClick={() => clearFilters(setAdvancedFilters)}
          marginRight="10px"
        >
          <Text color="gray.600">Limpar filtros</Text>
        </Button>

        <Button
          background="green.600"
          onClick={() => onChange(advancedFilters, handleCloseModal)}
          disabled={checkIfSubmitButtonIsDisabled()}
        >
          <Text color="white">Aplicar Filtros</Text>
        </Button>
      </Flex>
    </>
  );
};

export default DistribuicaoVarietalAdvancedFilters;
